// Hook: Mindful algo engine — wraps useAlgoEngine with guard checks
import { useState, useCallback, useRef, useEffect } from 'react';
import { useAlgoEngine } from './useAlgoEngine';
import { useMindGuards } from './useMindGuards';
import { useMind } from '../context/MindContext';

export function useMindfulAlgo() {
  const algo = useAlgoEngine();
  const { evaluateAlgoGuards } = useMindGuards();
  const { logBehavior } = useMind();
  const [autoPaused, setAutoPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState(null);
  const guardCheckRef = useRef(null);

  // Wrap startEngine to add guard checking interval
  const startEngine = useCallback((activeStrategies) => {
    setAutoPaused(false);
    setPauseReason(null);
    algo.startEngine(activeStrategies);
  }, [algo]);

  // Check guards periodically while engine is running
  useEffect(() => {
    if (!algo.running) {
      if (guardCheckRef.current) clearInterval(guardCheckRef.current);
      return;
    }

    guardCheckRef.current = setInterval(() => {
      const check = evaluateAlgoGuards();
      if (!check.allowed) {
        algo.stopEngine();
        setAutoPaused(true);
        setPauseReason(check.reason);
        logBehavior({
          type: 'ALGO_AUTO_PAUSED',
          reason: check.reason,
        });
      }
    }, 5000); // Check every 5 seconds

    return () => {
      if (guardCheckRef.current) clearInterval(guardCheckRef.current);
    };
  }, [algo.running, algo, evaluateAlgoGuards, logBehavior]);

  const resumeEngine = useCallback((activeStrategies) => {
    const check = evaluateAlgoGuards();
    if (!check.allowed) {
      setPauseReason(check.reason);
      return false;
    }
    setAutoPaused(false);
    setPauseReason(null);
    algo.startEngine(activeStrategies);
    return true;
  }, [algo, evaluateAlgoGuards]);

  return {
    ...algo,
    startEngine,
    resumeEngine,
    autoPaused,
    pauseReason,
  };
}
